import * as React from "react";
import { Inbox } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./Button";

/**
 * EmptyState — the shared warm/branded empty state for empty lists, tabs,
 * and zero-results views across every portal. Always gives a clear next
 * step. Use the `variant` to distinguish a genuinely empty list ("empty")
 * from a filtered no-results view ("noResults") — the latter should offer a
 * "clear filters" action rather than a create CTA.
 *
 * This is the pure, presentational base (no auth, no router). The staff
 * role-aware wrapper (`@/components/ux/EmptyState`) resolves a per-role
 * message and renders this.
 */
export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: EmptyStateAction;
  variant?: "empty" | "noResults";
  className?: string;
  children?: React.ReactNode;
}

export function EmptyState({
  title,
  description,
  icon: Icon = Inbox,
  action,
  variant = "empty",
  className,
  children,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center text-center py-space-7 px-space-5",
        "bg-white-pure border border-border-light rounded-lg shadow-sm",
        className,
      )}
      data-variant={variant}
    >
      <Icon size={40} strokeWidth={1.5} className="text-gold-shadow mb-space-3" aria-hidden />
      <h2 className="font-display font-bold text-h3 text-onyx m-0 mb-space-2 normal-case tracking-normal">
        {title}
      </h2>
      {description && (
        <p className="text-body text-gray-mid m-0 max-w-reading">{description}</p>
      )}
      {action && (
        <div className="mt-space-4">
          <Button
            variant={variant === "noResults" ? "secondary" : "primary"}
            onClick={() => {
              if (action.href) {
                window.location.assign(action.href);
              } else {
                action.onClick?.();
              }
            }}
          >
            {action.label}
          </Button>
        </div>
      )}
      {children}
    </div>
  );
}
